/**
 * Applications Copilot — speech-to-text hook.
 *
 * Primary: Azure SpeechRecognizer (lazy-loaded SDK) configured through the
 * same short-lived token as the TTS layer (`createSpeechConfig`), using
 * `recognizing` (interim) + `recognized` (final) events.
 * Fallback: react-speech-recognition (browser webkitSpeechRecognition) when
 * Azure is not configured / fails.
 *
 * Modes: 'ptt' (push-to-talk, hold) and 'handsfree' (toggle; auto-send after
 * 1.5 s of silence following a final result).
 *
 * Mic permission denial surfaces as error.code === 'mic_denied' with a
 * `retry()` the UI can wire to a button.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { createSpeechConfig } from './copilotTts';

export const SILENCE_MS = 1500;
const LANGUAGE = 'en-IN';

function hasMediaDevices() {
  return typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
}

/**
 * @param {object} opts
 * @param {(text:string)=>void} opts.onInterim   partial transcript (replace current interim)
 * @param {(text:string)=>void} opts.onFinal     final transcript segment (append)
 * @param {()=>void}            [opts.onAutoSend] hands-free: fired after 1.5 s silence
 * @param {()=>void}            [opts.onStart]    e.g. barge-in → stop TTS
 */
export function useCopilotVoice({ onInterim, onFinal, onAutoSend, onStart } = {}) {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('ptt');
  const [engine, setEngine] = useState(null); // 'azure' | 'browser' | null

  const recognizerRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const activeRef = useRef(false);
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const cbRef = useRef({ onInterim, onFinal, onAutoSend, onStart });
  cbRef.current = { onInterim, onFinal, onAutoSend, onStart };

  const browser = useSpeechRecognition();
  const browserSupported = Boolean(browser?.browserSupportsSpeechRecognition);
  const browserActiveRef = useRef(false);
  const lastBrowserFinalRef = useRef('');

  const supported = hasMediaDevices() || browserSupported;

  const clearSilenceTimer = () => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  };

  const armSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    if (modeRef.current !== 'handsfree') return;
    silenceTimerRef.current = setTimeout(() => {
      silenceTimerRef.current = null;
      if (cbRef.current.onAutoSend) cbRef.current.onAutoSend();
    }, SILENCE_MS);
  }, []);

  const stopAzure = useCallback(() => {
    const rec = recognizerRef.current;
    recognizerRef.current = null;
    if (!rec) return;
    try {
      rec.stopContinuousRecognitionAsync(
        () => {
          try {
            rec.close();
          } catch {
            // ignore
          }
        },
        () => {
          try {
            rec.close();
          } catch {
            // ignore
          }
        }
      );
    } catch {
      // ignore
    }
  }, []);

  const stopBrowser = useCallback(() => {
    if (!browserActiveRef.current) return;
    browserActiveRef.current = false;
    try {
      SpeechRecognition.stopListening();
    } catch {
      // ignore
    }
  }, []);

  const stop = useCallback(() => {
    activeRef.current = false;
    clearSilenceTimer();
    stopAzure();
    stopBrowser();
    setListening(false);
    setInterim('');
  }, [stopAzure, stopBrowser]);

  // Browser fallback: mirror react-speech-recognition transcript into callbacks.
  useEffect(() => {
    if (!browserActiveRef.current) return;
    const text = browser.interimTranscript || '';
    setInterim(text);
    if (cbRef.current.onInterim) cbRef.current.onInterim(text);
  }, [browser.interimTranscript]);

  useEffect(() => {
    if (!browserActiveRef.current) return;
    const finalText = browser.finalTranscript || '';
    if (!finalText || finalText === lastBrowserFinalRef.current) return;
    const added = finalText.startsWith(lastBrowserFinalRef.current)
      ? finalText.slice(lastBrowserFinalRef.current.length)
      : finalText;
    lastBrowserFinalRef.current = finalText;
    const segment = added.trim();
    if (segment) {
      setInterim('');
      if (cbRef.current.onFinal) cbRef.current.onFinal(segment);
      armSilenceTimer();
    }
  }, [browser.finalTranscript, armSilenceTimer]);

  const startBrowser = useCallback(async () => {
    if (!browserSupported) return false;
    try {
      browser.resetTranscript?.();
    } catch {
      // ignore
    }
    lastBrowserFinalRef.current = '';
    browserActiveRef.current = true;
    try {
      await SpeechRecognition.startListening({ continuous: true, language: LANGUAGE });
      setEngine('browser');
      return true;
    } catch {
      browserActiveRef.current = false;
      return false;
    }
  }, [browser, browserSupported]);

  const startAzure = useCallback(async () => {
    let ctx;
    try {
      ctx = await createSpeechConfig();
    } catch {
      return false;
    }
    if (!ctx || !activeRef.current) return false;
    const { SpeechSDK, config } = ctx;
    try {
      const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      const recognizer = new SpeechSDK.SpeechRecognizer(config, audioConfig);
      recognizerRef.current = recognizer;

      recognizer.recognizing = (_s, e) => {
        if (!activeRef.current) return;
        const text = e?.result?.text || '';
        clearSilenceTimer();
        setInterim(text);
        if (cbRef.current.onInterim) cbRef.current.onInterim(text);
      };
      recognizer.recognized = (_s, e) => {
        if (!activeRef.current) return;
        const reason = e?.result?.reason;
        const isSpeech = !SpeechSDK.ResultReason || reason === SpeechSDK.ResultReason.RecognizedSpeech;
        const text = (e?.result?.text || '').trim();
        setInterim('');
        if (cbRef.current.onInterim) cbRef.current.onInterim('');
        if (isSpeech && text && cbRef.current.onFinal) cbRef.current.onFinal(text);
        armSilenceTimer();
      };
      recognizer.canceled = (_s, e) => {
        const details = e?.errorDetails || '';
        if (/permission|microphone|NotAllowed/i.test(details)) {
          setError({ code: 'mic_denied', message: 'Microphone access was blocked. Allow the mic and try again.' });
        } else if (details) {
          setError({ code: 'stt_failed', message: 'Speech recognition stopped unexpectedly.' });
        }
        stop();
      };
      recognizer.sessionStopped = () => {
        if (activeRef.current) stop();
      };

      await new Promise((resolve, reject) => {
        recognizer.startContinuousRecognitionAsync(resolve, reject);
      });
      setEngine('azure');
      return true;
    } catch {
      stopAzure();
      return false;
    }
  }, [armSilenceTimer, stop, stopAzure]);

  const start = useCallback(async () => {
    if (activeRef.current) return;
    setError(null);
    if (!supported) {
      setError({ code: 'unsupported', message: 'Voice input is not supported in this browser.' });
      return;
    }
    activeRef.current = true;
    if (cbRef.current.onStart) cbRef.current.onStart();

    // Prompt for the mic up-front so denial is a clear, retryable state.
    if (hasMediaDevices()) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
      } catch (err) {
        activeRef.current = false;
        const denied = err?.name === 'NotAllowedError' || err?.name === 'SecurityError';
        setError(
          denied
            ? { code: 'mic_denied', message: 'Microphone access was blocked. Allow the mic in your browser and retry.' }
            : { code: 'mic_unavailable', message: 'No microphone was found.' }
        );
        return;
      }
    }
    if (!activeRef.current) return; // stopped while awaiting permission

    setListening(true);
    const okAzure = await startAzure();
    if (okAzure) return;
    if (!activeRef.current) return;
    const okBrowser = await startBrowser();
    if (!okBrowser) {
      setError({ code: 'stt_unavailable', message: 'Voice input is unavailable right now. You can still type.' });
      stop();
    }
  }, [supported, startAzure, startBrowser, stop]);

  const toggle = useCallback(() => {
    if (activeRef.current) stop();
    else void start();
  }, [start, stop]);

  const retry = useCallback(() => {
    setError(null);
    void start();
  }, [start]);

  useEffect(() => () => stop(), [stop]);

  return {
    listening,
    interim,
    start,
    stop,
    toggle,
    retry,
    supported,
    error,
    mode,
    setMode,
    engine,
  };
}

export default useCopilotVoice;
